import { chain } from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import {
  CareType,
  PeriodType,
  OrganizationCarePeriodsQuery,
  useOrganizationCarePeriodsQuery,
} from '../../../graphQL';
import { useDrilldownContext } from '../../../Pages/Users/helpers';
import { isMantraAdmin, useCurrentProvider } from '../../Permissions';

type HookProps = {
  careType?: CareType | null;
};

export const useCocEligibility = ({ careType }: HookProps) => {
  const { user } = useDrilldownContext();
  const { currentProvider } = useCurrentProvider();
  const [cocDayThreshold, setCocDayThreshold] = useState<number>(0);
  const [carePeriods, setCarePeriods] = useState<
    OrganizationCarePeriodsQuery['organization']['carePeriods']
  >([]);

  useOrganizationCarePeriodsQuery({
    variables: { organizationId: user.organization?.id! },
    notifyOnNetworkStatusChange: true,
    onCompleted: d => {
      setCarePeriods(d?.organization.carePeriods || []);
      setCocDayThreshold(d?.continuityOfCareThreshold || 0);
    },
    skip: !user.organization || !careType,
  });

  const currentCarePeriod = carePeriods?.length
    ? carePeriods.find(c => moment().isBetween(c.startDate, c.endDate, 'days', '[]'))
    : null;
  const nextCarePeriod = carePeriods?.length
    ? carePeriods.find(c => moment(c.startDate).isAfter(currentCarePeriod?.endDate, 'days'))
    : null;
  const carePeriodEnding =
    moment(currentCarePeriod?.startDate).diff(moment(), 'days') < cocDayThreshold;
  const firstDayOfBreak =
    nextCarePeriod && isPeriodBreak(nextCarePeriod)
      ? moment(nextCarePeriod.startDate).format('M/D/YY')
      : null;
  const lastDayOfBreak =
    nextCarePeriod && isPeriodBreak(nextCarePeriod)
      ? moment(nextCarePeriod.endDate).format('M/D/YY')
      : null;
  const lastDayOfTerm = currentCarePeriod
    ? moment(currentCarePeriod.endDate).format('M/D/YY')
    : null;
  const endingDueToBreak = carePeriodEnding && nextCarePeriod && isPeriodBreak(nextCarePeriod);

  const latestEligibilityResponse = chain(user)
    .get('continuityOfCareEligibility')
    .filter(e => e.careType === careType)
    .find(e => moment(e.updatedAt).isBefore(currentCarePeriod?.endDate))
    .value();

  const assignedProviderId = user.careFlows?.find(cf => cf.careType === careType)?.provider?.id;
  const isAssignedProviderForCareType = assignedProviderId === currentProvider.id;

  const showEligibilityData =
    isAssignedProviderForCareType || isMantraAdmin(currentProvider)
      ? user.cocEligibility.find(e => e.careType === careType)
      : null;

  const hasNextStepsSelected = !!user?.continuityOfCareTransitions?.filter(
    c => c.careType === careType
  )?.length;

  const remainingSessions = user?.careFlows.find(c => c.careType === careType)?.careSessionInfo
    ?.remainingAppts;

  return {
    cocDayThreshold,
    currentCarePeriod,
    endingDueToBreak,
    firstDayOfBreak,
    hasFilledOutEligibilityForm: !!latestEligibilityResponse,
    hasNextStepsSelected,
    lastDayOfBreak,
    lastDayOfTerm,
    latestEligibilityResponse,
    nextCarePeriod,
    remainingSessions,
    showEligibilityModule: !!showEligibilityData,
    showSponsoredForm: showEligibilityData?.paymentType === 'sponsored',
  };
};

type CarePeriod = OrganizationCarePeriodsQuery['organization']['carePeriods'][number];
const isPeriodBreak = (cp: Pick<CarePeriod, 'periodType'>) =>
  cp.periodType === PeriodType.SponsoredBreak || cp.periodType === PeriodType.NonSponsoredBreak;
